import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsAPI, pushAPI } from '../lib/api';
import { Bell, X, Check, CheckCheck, Info, BellRing, Trash2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const SEVERITY_CONFIG = {
  CRITIQUE: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700', badge: 'bg-red-500 text-white' },
  ATTENTION: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-500 text-white' },
  INFO: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700', badge: 'bg-emerald-500 text-white' },
};

function NotifCard({ notif, onDismiss, onNavigate }) {
  const sc = SEVERITY_CONFIG[notif.severity] || SEVERITY_CONFIG.INFO;
  const d = new Date(notif.created_at);
  const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`p-3 rounded-xl border transition-all ${sc.bg} ${sc.border}`} data-testid={`notif-${notif.id}`}>
      <div className="flex gap-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${sc.badge}`}>
          <Info className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-xs font-bold ${sc.text}`}>{notif.title}</h4>
            <span className="text-[10px] text-slate-400 shrink-0">{timeStr}</span>
          </div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600 line-clamp-2">{notif.body}</p>
          <div className="mt-2 flex items-center gap-1.5">
            {notif.url && notif.url !== '/' && (
              <button
                onClick={() => onNavigate(notif.url)}
                className="flex items-center gap-1 text-[10px] font-bold text-[#FF5C00] hover:underline"
                data-testid={`notif-goto-${notif.id}`}
              >
                <ExternalLink className="h-3 w-3" /> Voir
              </button>
            )}
            <button
              onClick={() => onDismiss(notif.id)}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-red-500 ml-auto"
              data-testid={`notif-dismiss-${notif.id}`}
            >
              <Trash2 className="h-3 w-3" /> Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertCard({ alert, onRead }) {
  const sc = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.INFO;
  const d = new Date(alert.triggered_at);
  const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const dateStr = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

  return (
    <div
      className={`p-3 rounded-xl border transition-all cursor-pointer ${alert.is_read ? 'bg-surface-elev border-slate-100 opacity-60' : `${sc.bg} ${sc.border}`}`}
      onClick={() => !alert.is_read && onRead(alert.id)}
      data-testid={`alert-${alert.id}`}
    >
      <div className="flex gap-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${alert.is_read ? 'bg-surface-muted text-slate-400' : sc.badge}`}>
          <Info className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-xs font-bold ${alert.is_read ? 'text-slate-400' : sc.text}`}>{alert.title}</h4>
            <span className="text-[10px] text-slate-400 shrink-0">{dateStr} {timeStr}</span>
          </div>
          <p className={`mt-0.5 text-[11px] leading-relaxed ${alert.is_read ? 'text-slate-400' : 'text-slate-600'}`}>
            {(alert.message || '').split('\n')[0]}
          </p>
          {!alert.is_read && (
            <p className="mt-1.5 text-[10px] text-slate-400 flex items-center gap-1"><Check className="h-3 w-3" /> Cliquer pour marquer lu</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('notifs'); // 'notifs' | 'alerts'
  const [notifications, setNotifications] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const prevCountRef = useRef(0);
  const initialLoadRef = useRef(true);

  const [pushStatus, setPushStatus] = useState('unknown');

  // Son de notification via Web Audio API
  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Note 1 — ding
      const o1 = ctx.createOscillator();
      const g1 = ctx.createGain();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(880, ctx.currentTime);
      o1.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.08);
      g1.gain.setValueAtTime(0.15, ctx.currentTime);
      g1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      o1.connect(g1).connect(ctx.destination);
      o1.start(ctx.currentTime);
      o1.stop(ctx.currentTime + 0.4);
      // Note 2 — dong (harmonique)
      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(1320, ctx.currentTime + 0.1);
      g2.gain.setValueAtTime(0.1, ctx.currentTime + 0.1);
      g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      o2.connect(g2).connect(ctx.destination);
      o2.start(ctx.currentTime + 0.1);
      o2.stop(ctx.currentTime + 0.6);
      setTimeout(() => ctx.close(), 700);
    } catch { /* AudioContext not available */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const r = await pushAPI.getNotifications();
      const newNotifs = r.data?.notifications || [];
      setNotifications(prev => {
        const newCount = newNotifs.length;
        // Play sound only if count increased (not on first load)
        if (!initialLoadRef.current && newCount > prevCountRef.current) {
          playNotifSound();
        }
        initialLoadRef.current = false;
        prevCountRef.current = newCount;
        return newNotifs;
      });
    } catch { /* silent */ }
  }, [playNotifSound]);

  const fetchAlerts = useCallback(async () => {
    try {
      const r = await alertsAPI.getAlerts();
      setAlerts(r.data?.alerts || []);
    } catch { /* silent */ }
  }, []);

  const fetchAlertCount = useCallback(async () => {
    try {
      const r = await alertsAPI.getCount();
      setAlertCount(r.data?.count || 0);
    } catch { /* silent */ }
  }, []);

  // Poll counts every 60s
  useEffect(() => {
    fetchAlertCount();
    fetchNotifications();
    const interval = setInterval(() => { fetchAlertCount(); fetchNotifications(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchAlertCount, fetchNotifications]);

  // Listen for push messages from service worker — instant sound + refresh
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'NEW_NOTIFICATION') {
        playNotifSound();
        fetchNotifications();
        fetchAlertCount();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [playNotifSound, fetchNotifications, fetchAlertCount]);

  // Load data when panel opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.all([fetchNotifications(), fetchAlerts()]).finally(() => setLoading(false));
    }
  }, [open, fetchNotifications, fetchAlerts]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Push setup
  useEffect(() => {
    async function setupPush() {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushStatus('unsupported'); return; }
      const permission = Notification.permission;
      if (permission === 'denied') { setPushStatus('denied'); return; }
      try {
        let reg = await navigator.serviceWorker.ready.catch(() => null);
        if (!reg) reg = await navigator.serviceWorker.register('/sw.js?v=6');
        const existing = await reg.pushManager.getSubscription();
        if (existing) { setPushStatus('granted'); return; }
        if (permission === 'granted') {
          const r = await pushAPI.getVapidKey();
          const pubKey = r.data?.public_key;
          if (pubKey) {
            const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(pubKey) });
            await pushAPI.subscribe(sub.toJSON());
            setPushStatus('granted');
          }
        } else { setPushStatus('default'); }
      } catch { setPushStatus('unsupported'); }
    }
    setupPush();
  }, []);

  const requestPushPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const reg = await navigator.serviceWorker.ready;
        const r = await pushAPI.getVapidKey();
        const pubKey = r.data?.public_key;
        if (pubKey) {
          const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(pubKey) });
          await pushAPI.subscribe(sub.toJSON());
          setPushStatus('granted');
        }
      } else { setPushStatus('denied'); }
    } catch { /* silent */ }
  };

  const handleDismiss = async (id) => {
    try {
      await pushAPI.dismissNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch { /* silent */ }
  };

  const handleDismissAll = async () => {
    try {
      await pushAPI.dismissAll();
      setNotifications([]);
    } catch { /* silent */ }
  };

  const handleReadAlert = async (id) => {
    try {
      await alertsAPI.markRead(id);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
      setAlertCount(c => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const handleReadAllAlerts = async () => {
    try {
      await alertsAPI.markAllRead();
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      setAlertCount(0);
    } catch { /* silent */ }
  };

  const handleNavigate = (url) => {
    setOpen(false);
    navigate(url);
  };

  const totalCount = notifications.length + alertCount;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-xl hover:bg-surface-muted transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`h-5 w-5 ${totalCount > 0 ? 'text-[#FF5C00]' : 'text-slate-400'}`} />
        {totalCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse"
            data-testid="notification-badge">
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-4 top-20 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-[400px] max-h-[75vh] bg-surface-elev rounded-2xl shadow-2xl border border-hairline overflow-hidden flex flex-col"
          style={{ zIndex: 9990 }}
          data-testid="notification-panel">

          {/* Tabs */}
          <div className="flex border-b border-slate-100">
            <button
              onClick={() => setTab('notifs')}
              className={`flex-1 py-3 text-xs font-bold text-center transition-colors relative ${tab === 'notifs' ? 'text-[#FF5C00]' : 'text-slate-400 hover:text-slate-600'}`}
              data-testid="tab-notifications"
            >
              Notifications {notifications.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px]">{notifications.length}</span>}
              {tab === 'notifs' && <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-[#FF5C00] rounded-full" />}
            </button>
            <button
              onClick={() => setTab('alerts')}
              className={`flex-1 py-3 text-xs font-bold text-center transition-colors relative ${tab === 'alerts' ? 'text-[#FF5C00]' : 'text-slate-400 hover:text-slate-600'}`}
              data-testid="tab-alerts"
            >
              Alertes {alertCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 text-[10px]">{alertCount}</span>}
              {tab === 'alerts' && <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-[#FF5C00] rounded-full" />}
            </button>
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#FF5C00] border-t-transparent" />
              </div>
            ) : tab === 'notifs' ? (
              notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Aucune notification aujourd'hui</p>
                  <p className="text-[10px] text-slate-300 mt-1">Les notifications du jour apparaissent ici</p>
                </div>
              ) : (
                notifications.map(n => <NotifCard key={n.id} notif={n} onDismiss={handleDismiss} onNavigate={handleNavigate} />)
              )
            ) : (
              alerts.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Aucune alerte</p>
                </div>
              ) : (
                alerts.map(a => <AlertCard key={a.id} alert={a} onRead={handleReadAlert} />)
              )
            )}
          </div>

          {/* Footer actions */}
          <div className="border-t border-slate-100">
            {tab === 'notifs' && notifications.length > 0 && (
              <div className="px-4 py-2 flex justify-center">
                <Button variant="ghost" size="sm" onClick={handleDismissAll}
                  className="h-7 text-[10px] text-slate-400 hover:text-red-500 gap-1"
                  data-testid="dismiss-all-notifs">
                  <Trash2 className="h-3 w-3" /> Tout supprimer
                </Button>
              </div>
            )}
            {tab === 'alerts' && alertCount > 0 && (
              <div className="px-4 py-2 flex justify-center">
                <Button variant="ghost" size="sm" onClick={handleReadAllAlerts}
                  className="h-7 text-[10px] text-slate-400 hover:text-[#FF5C00] gap-1"
                  data-testid="mark-all-read">
                  <CheckCheck className="h-3 w-3" /> Tout marquer lu
                </Button>
              </div>
            )}

            {/* Push banner */}
            {pushStatus === 'default' && (
              <div className="px-4 py-3 border-t border-slate-100 bg-gradient-to-r from-amber-50 to-orange-50">
                <button onClick={requestPushPermission}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl bg-[#FF5C00] text-white text-xs font-bold hover:bg-[#E55200] transition-colors"
                  data-testid="enable-push-btn">
                  <BellRing className="h-3.5 w-3.5" /> Activer les notifications push
                </button>
              </div>
            )}
            {pushStatus === 'granted' && (
              <div className="px-4 py-1.5 flex items-center justify-center gap-1.5 text-[10px] text-emerald-600">
                <Check className="h-3 w-3" /> Push actives
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
